import { supabase } from '@/integrations/supabase/client';

/**
 * Impersonation session plumbing, deliberately hook-free.
 *
 * This lives outside `useImpersonate.ts` because `AuthProvider` needs `endImpersonation`,
 * and `useImpersonate.ts` imports `useAuth` — which comes from `AuthProvider`. Importing it
 * back would close an import cycle. Nothing here touches React, so both can depend on it.
 * It is also the single home for the storage key, which used to be spelled out in three
 * separate files.
 */

export const IMPERSONATION_KEY = 'impersonating_from';

export interface StoredSession {
  userId: string;
  email: string;
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

/** True while an admin session is stashed, whether or not it has expired. */
export function hasStoredImpersonation(): boolean {
  return sessionStorage.getItem(IMPERSONATION_KEY) !== null;
}

export interface ImpersonationState {
  isImpersonating: boolean;
  /** Set when a stash exists but its 1-hour window has passed. */
  isExpired: boolean;
  originalAdmin: StoredSession | null;
}

/**
 * Read the stashed admin session.
 *
 * Unlike the original implementation this does NOT delete an expired stash. Deleting it made
 * the impersonation bar disappear exactly when the operator was most stuck — no banner, no exit,
 * and `endImpersonation`'s own expiry branch became unreachable because the key was already
 * gone. Expiry is now reported so the UI can show it and still offer a way out (#2836).
 */
export function readImpersonationState(): ImpersonationState {
  const stored = sessionStorage.getItem(IMPERSONATION_KEY);
  if (!stored) return { isImpersonating: false, isExpired: false, originalAdmin: null };

  try {
    const originalAdmin = JSON.parse(stored) as StoredSession;
    const isExpired = !!originalAdmin.expires_at && originalAdmin.expires_at < Date.now();
    return { isImpersonating: true, isExpired, originalAdmin };
  } catch {
    // Unparseable stash is worse than none — drop it so the app isn't wedged.
    sessionStorage.removeItem(IMPERSONATION_KEY);
    return { isImpersonating: false, isExpired: false, originalAdmin: null };
  }
}

/**
 * Verify a stashed impersonation session is still a genuine, live Supabase session — not
 * just JSON sitting in sessionStorage, which any script (or a non-admin user's own devtools)
 * can edit.
 *
 * Returns the id Supabase Auth itself resolves from the token, never the possibly-tampered
 * `userId` field off the stash. Returns null on any failure — expired, revoked, network
 * error, mismatched id — so every caller fails closed by construction.
 */
export async function verifyStashedAdmin(stored: StoredSession): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.getUser(stored.access_token);
    if (error || !data.user) return null;
    if (data.user.id !== stored.userId) return null;
    return data.user.id;
  } catch {
    return null;
  }
}

/** Sign out for real, clearing any impersonation stash first. */
async function signOutToAuth(): Promise<void> {
  sessionStorage.removeItem(IMPERSONATION_KEY);
  await supabase.auth.signOut();
  window.location.href = '/auth';
}

let exitInFlight = false;

/**
 * Restore the admin session that was stashed before impersonating.
 *
 * Every failure path falls back to a real sign-out rather than leaving the operator inside
 * someone else's session.
 */
export async function endImpersonation(): Promise<void> {
  // Re-entrancy guard. The stash is cleared before the awaits below, so a second concurrent
  // call would fall into the "no stash" branch and sign out for real — stranding the operator,
  // which is the bug this whole change removes. A double-tap on a slow connection is enough.
  if (exitInFlight) return;
  exitInFlight = true;

  try {
    const stored = sessionStorage.getItem(IMPERSONATION_KEY);
    if (!stored) {
      await signOutToAuth();
      return;
    }

    const originalSession = JSON.parse(stored) as StoredSession;

    // Clear the stash first so a failure below can't leave a stale banner behind.
    sessionStorage.removeItem(IMPERSONATION_KEY);

    if (originalSession.expires_at && originalSession.expires_at < Date.now()) {
      await signOutToAuth();
      return;
    }

    const { error } = await supabase.auth.setSession({
      access_token: originalSession.access_token,
      refresh_token: originalSession.refresh_token,
    });
    if (error) {
      await signOutToAuth();
      return;
    }

    // The stashed token may have been revoked while we were away.
    const { error: userError } = await supabase.auth.getUser();
    if (userError) {
      await signOutToAuth();
      return;
    }

    // Full reload so every cached query is refetched as the admin.
    window.location.href = '/dashboard';
  } catch {
    exitInFlight = false;
    await signOutToAuth();
  }
  // Deliberately NOT reset on the success path. Assigning window.location.href does not block,
  // so clearing the flag here would reopen the very race the guard closes: the stash is already
  // gone, and a second exit in that window would fall through to a real sign-out of the admin
  // session we just restored. The page is navigating away; the flag dies with it.
}
